/**
 * Playbooks page: MSP-wide and customer-specific operating instructions. Ticket authorizations are
 * configured per agent on the agent's configure screen.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import DOMPurify from "dompurify";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { marked } from "marked";
import IntegrationApi from "../apis/integrationApi";
import GlobalLoadingOverlay from "../components/GlobalLoadingOverlay";
import LayeredAutocompleteSelect from "../components/LayeredAutocompleteSelect";
import TheoCategoryAutocomplete, {
  buildTheoTaxonomyOptions,
  formatTheoCategorySelectionLabel,
  type TheoTaxonomyOption,
} from "../components/TheoCategoryAutocomplete";
import { useUserMsp } from "../hooks/useUserMsp";
import {
  ApplicationPlaybook,
  ApplicationPlaybookEntry,
  Customer,
  PlaybookWritePayload,
} from "../types/integration";
import { formatAppName } from "../utils/connectorUtils";
import { NEUTRAL } from "../styles/brandTokens";

const integrationApi = new IntegrationApi();

type PlaybooksTab = "msp" | "customer";

type AlertState = {
  open: boolean;
  message: string;
  severity: "success" | "error";
};

const toRows = (
  response: { rows?: ApplicationPlaybookEntry[] } | ApplicationPlaybookEntry[]
): ApplicationPlaybookEntry[] => (Array.isArray(response) ? response : response?.rows ?? []);

const getEntryKey = (entry: ApplicationPlaybookEntry) =>
  `${entry.entity_type}:${entry.reference_id}`;

const getEntryTitle = (entry: ApplicationPlaybookEntry) => {
  if (entry.ownership_scope === "customer") {
    return `${entry.customer_name || "Customer"} playbooks`;
  }
  return formatAppName(entry.name);
};

const getEntrySubtitle = (entry: ApplicationPlaybookEntry) => {
  if (entry.ownership_scope === "customer") {
    return `${entry.customer_name || "Selected customer"} · General`;
  }
  if (entry.entity_type === "general") {
    return "Available for every ticket and conversation";
  }
  if (entry.entity_type === "platform") {
    return entry.platform_type
      ? `${entry.platform_type.toUpperCase()} platform`
      : "Platform";
  }

  return "MSP-level application playbook";
};

const getPlaybookSourceLabel = (playbook: ApplicationPlaybook) => {
  if (playbook.ownership_scope === "customer" || playbook.customer_id) {
    return "Customer-specific";
  }
  if (playbook.is_disabled) {
    return "MSP disabled";
  }
  return playbook.is_global_default ? "Global default" : "MSP override";
};

const getPlaybookKey = (entry: ApplicationPlaybookEntry, playbook: ApplicationPlaybook, index: number) => {
  const idPart = playbook.skill_id ?? playbook.global_skill_id ?? (playbook.skill_slug || "new");
  return `${getEntryKey(entry)}:${idPart}:${index}`;
};

const isGeneralPlaybookEntry = (entry: ApplicationPlaybookEntry) =>
  entry.entity_type === "general";

const createBlankPlaybook = (sortOrder: number): ApplicationPlaybook => ({
  skill_id: null,
  global_skill_id: null,
  skill_slug: "",
  name: "New playbook",
  description: "",
  skill_text: "",
  enabled: true,
  sort_order: sortOrder,
  is_global_default: false,
  is_disabled: false,
  theo_categories: [],
});

const renderMarkdown = (value: string) =>
  DOMPurify.sanitize(
    String(
      marked.parse(value || "*Nothing to preview yet.*", {
        breaks: true,
      })
    )
  );

const ApplicationPlaybooks: React.FC = () => {
  const { userMspInfo } = useUserMsp();
  const [configurationScope, setConfigurationScope] = useState<PlaybooksTab>("msp");
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [entries, setEntries] = useState<ApplicationPlaybookEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [dirtyPlaybooks, setDirtyPlaybooks] = useState<Record<string, boolean>>({});
  const [editorModes, setEditorModes] = useState<Record<string, "write" | "preview">>(
    {}
  );
  const [expandedPlaybooks, setExpandedPlaybooks] = useState<Record<string, boolean>>({});
  const [pinEnabled, setPinEnabled] = useState<Record<string, boolean>>({});
  const [theoTaxonomyOptions, setTheoTaxonomyOptions] = useState<TheoTaxonomyOption[]>([]);
  const [alert, setAlert] = useState<AlertState>({
    open: false,
    message: "",
    severity: "success",
  });

  const mspId = userMspInfo?.msp_id;
  const customerOptions = useMemo(
    () =>
      customers
        .filter((customer) => customer.name?.trim().toLowerCase() !== "root")
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
        .map((customer) => ({ label: customer.name, value: String(customer.id) })),
    [customers]
  );

  const loadPlaybooks = useCallback(async () => {
    if (!mspId) {
      setEntries([]);
      setIsLoading(false);
      return;
    }

    if (configurationScope === "customer" && !selectedCustomerId) {
      setEntries([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = (configurationScope === "customer"
        ? await integrationApi.getCustomerPlaybooks(selectedCustomerId)
        : await integrationApi.getApplicationPlaybooks(mspId)) as
        | { rows?: ApplicationPlaybookEntry[] }
        | ApplicationPlaybookEntry[];
      setEntries(toRows(response));
      setPinEnabled({});
    } catch (err: any) {
      setError(err?.message || "Unable to load playbooks.");
    } finally {
      setIsLoading(false);
    }
  }, [configurationScope, mspId, selectedCustomerId]);

  useEffect(() => {
    loadPlaybooks();
  }, [loadPlaybooks]);

  useEffect(() => {
    if (!mspId) {
      setCustomers([]);
      return;
    }
    let active = true;
    setCustomersLoading(true);
    integrationApi
      .getCustomersByMspId(String(mspId))
      .then((response: any) => {
        if (active) {
          setCustomers(Array.isArray(response) ? response : response?.rows ?? []);
        }
      })
      .catch(() => {
        if (active) {
          setCustomers([]);
        }
      })
      .finally(() => {
        if (active) {
          setCustomersLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [mspId]);

  useEffect(() => {
    let active = true;
    integrationApi
      .getTheoTicketTaxonomy()
      .then((response) => {
        if (active) {
          setTheoTaxonomyOptions(buildTheoTaxonomyOptions(response.categories || []));
        }
      })
      .catch(() => {
        if (active) {
          setTheoTaxonomyOptions([]);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  const hasEntries = useMemo(() => entries.length > 0, [entries.length]);

  const updatePlaybookAtIndex = (
    entryKey: string,
    playbookIndex: number,
    updates: Partial<ApplicationPlaybook>
  ) => {
    const dirtyKey = `${entryKey}:${playbookIndex}`;
    setEntries((currentEntries) =>
      currentEntries.map((entry) =>
        getEntryKey(entry) === entryKey
          ? {
              ...entry,
              skills: entry.skills.map((skill, index) =>
                index === playbookIndex ? { ...skill, ...updates } : skill
              ),
            }
          : entry
      )
    );
    setDirtyPlaybooks((current) => ({ ...current, [dirtyKey]: true }));
  };

  const handleAddPlaybook = (entry: ApplicationPlaybookEntry) => {
    const entryKey = getEntryKey(entry);
    const newIndex = entry.skills.length;
    setEntries((currentEntries) =>
      currentEntries.map((currentEntry) =>
        getEntryKey(currentEntry) === entryKey
          ? {
              ...currentEntry,
              skills: [
                ...currentEntry.skills,
                {
                  ...createBlankPlaybook((currentEntry.skills.length + 1) * 10),
                  customer_id: currentEntry.customer_id,
                  ownership_scope: currentEntry.ownership_scope,
                },
              ],
            }
          : currentEntry
      )
    );
    setExpandedPlaybooks((current) => ({
      ...current,
      [`${entryKey}:new:${newIndex}`]: true,
    }));
    setDirtyPlaybooks((current) => ({ ...current, [`${entryKey}:${newIndex}`]: true }));
  };

  const setEditorMode = (entryKey: string, mode: "write" | "preview") => {
    setEditorModes((currentModes) => ({
      ...currentModes,
      [entryKey]: mode,
    }));
  };

  const togglePlaybookExpanded = (playbookKey: string) => {
    setExpandedPlaybooks((current) => ({
      ...current,
      [playbookKey]: !(current[playbookKey] ?? false),
    }));
  };

  const handleSave = async (
    entry: ApplicationPlaybookEntry,
    playbook: ApplicationPlaybook,
    playbookIndex: number
  ) => {
    if (!mspId) {
      return;
    }

    const playbookKey = getPlaybookKey(entry, playbook, playbookIndex);
    setBusyKey(playbookKey);

    try {
      const payload: PlaybookWritePayload = {
        name: playbook.name,
        description: playbook.description,
        skill_text: playbook.skill_text,
        skill_slug: playbook.skill_slug || undefined,
        enabled: playbook.enabled,
        sort_order: playbook.sort_order ?? playbookIndex * 10,
      };
      if (isGeneralPlaybookEntry(entry)) {
        payload.theo_categories = playbook.theo_categories || [];
      }

      if (playbook.skill_id) {
        await integrationApi.updatePlaybook(playbook.skill_id, payload);
      } else if (entry.ownership_scope === "customer" && selectedCustomerId) {
        await integrationApi.createCustomerPlaybook(selectedCustomerId, payload);
      } else if (entry.entity_type === "general") {
        await integrationApi.createGeneralPlaybook(payload);
      } else if (entry.entity_type === "platform") {
        await integrationApi.createPlatformPlaybook(
          entry.reference_id,
          mspId,
          payload
        );
      } else {
        await integrationApi.createApplicationPlaybook(
          entry.reference_id,
          mspId,
          payload
        );
      }

      setAlert({
        open: true,
        message: `${playbook.name} saved.`,
        severity: "success",
      });
      setDirtyPlaybooks((current) => ({ ...current, [`${getEntryKey(entry)}:${playbookIndex}`]: false }));
      await loadPlaybooks();
    } catch (err: any) {
      setAlert({
        open: true,
        message: err?.message || "Failed to save playbook.",
        severity: "error",
      });
    } finally {
      setBusyKey(null);
    }
  };

  const handleNormalize = async (
    entry: ApplicationPlaybookEntry,
    playbook: ApplicationPlaybook,
    playbookIndex: number
  ) => {
    const playbookKey = getPlaybookKey(entry, playbook, playbookIndex);
    const skillText = (playbook.skill_text || "").trim();
    if (!skillText) {
      setAlert({
        open: true,
        message: "Write playbook instructions before normalizing them.",
        severity: "error",
      });
      return;
    }

    setBusyKey(playbookKey);
    try {
      const result = await integrationApi.normalizePlaybook({
        name: playbook.name,
        description: playbook.description,
        skill_text: skillText,
        entity_type: entry.ownership_scope === "customer" ? "customer" : entry.entity_type,
        entity_name: getEntryTitle(entry),
      });
      updatePlaybookAtIndex(getEntryKey(entry), playbookIndex, {
        name: result.name,
        description: result.description,
        skill_text: result.skill_text,
      });
      setEditorMode(playbookKey, "write");
      setAlert({
        open: true,
        message: "Playbook normalized. Review the draft, then save when ready.",
        severity: "success",
      });
    } catch (err: any) {
      setAlert({
        open: true,
        message: err?.message || "Failed to normalize playbook.",
        severity: "error",
      });
    } finally {
      setBusyKey(null);
    }
  };

  const handleDelete = async (
    entry: ApplicationPlaybookEntry,
    playbook: ApplicationPlaybook,
    playbookIndex: number
  ) => {
    if (!mspId || !window.confirm(`Delete "${playbook.name}"?`)) {
      return;
    }

    if (!playbook.skill_id && playbook.global_skill_id) {
      setAlert({
        open: true,
        message: "Global defaults cannot be deleted here. Disable it to create an MSP-level suppression.",
        severity: "error",
      });
      return;
    }

    const entryKey = getEntryKey(entry);
    const playbookKey = getPlaybookKey(entry, playbook, playbookIndex);
    setBusyKey(playbookKey);

    try {
      if (playbook.skill_id) {
        await integrationApi.deletePlaybook(playbook.skill_id);
      } else {
        setEntries((currentEntries) =>
          currentEntries.map((currentEntry) =>
            getEntryKey(currentEntry) === entryKey
              ? {
                  ...currentEntry,
                  skills: currentEntry.skills.filter((_, index) => index !== playbookIndex),
                }
              : currentEntry
          )
        );
      }

      setAlert({
        open: true,
        message: playbook.skill_id
          ? `${playbook.name} ${entry.ownership_scope === "customer" ? "customer playbook" : "MSP override"} deleted.`
          : `${playbook.name} removed.`,
        severity: "success",
      });
      await loadPlaybooks();
    } catch (err: any) {
      setAlert({
        open: true,
        message: err?.message || "Failed to delete playbook.",
        severity: "error",
      });
    } finally {
      setBusyKey(null);
    }
  };

  const handleToggleEnabled = async (
    entry: ApplicationPlaybookEntry,
    playbook: ApplicationPlaybook,
    playbookIndex: number
  ) => {
    await handleSave(
      entry,
      {
        ...playbook,
        enabled: !playbook.enabled,
      },
      playbookIndex
    );
  };

  if (isLoading) {
    return (
      <Box
        width="100%"
        sx={{
          p: 1,
          background: NEUTRAL.page,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "240px",
        }}
      >
        <GlobalLoadingOverlay />
      </Box>
    );
  }

  return (
    <Box
      width="100%"
      sx={{ p: 3, background: NEUTRAL.page, height: "100%", overflowY: "auto" }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3, fontFamily: "var(--theo-font-sans)" }}>
          {error}
        </Alert>
      )}

      <Paper
        variant="outlined"
        sx={{ mb: 3, borderRadius: "14px", overflow: "hidden", borderColor: "#E5E7EB" }}
      >
        <Tabs
          value={configurationScope}
          onChange={(_, value: PlaybooksTab) => setConfigurationScope(value)}
          aria-label="Playbook ownership scope"
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{
            px: 1,
            "& .MuiTab-root": {
              minHeight: 58,
              fontFamily: "var(--theo-font-sans)",
              fontWeight: 700,
              textTransform: "none",
            },
            "& .Mui-selected": { color: "#705D4B !important" },
            "& .MuiTabs-indicator": { backgroundColor: "#705D4B", height: 3 },
          }}
        >
          <Tab value="msp" label="MSP-wide playbooks" />
          <Tab value="customer" label="Customer-specific playbooks" />
        </Tabs>
      </Paper>

      {configurationScope === "customer" && (
        <Paper
          variant="outlined"
          sx={{ mb: 3, p: 3, borderRadius: "10px", borderColor: "#E5E7EB" }}
        >
          <Stack spacing={2}>
            <Box>
              <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: "18px", fontWeight: 700, color: "#111827" }}>
                Choose a customer
              </Typography>
              <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: "14px", color: "#6B7280", mt: 0.5 }}>
                Customer playbooks are General instructions and only enter the catalog for the selected customer.
              </Typography>
            </Box>
            <LayeredAutocompleteSelect
              label="Customer"
              value={selectedCustomerId}
              options={customerOptions}
              onChange={setSelectedCustomerId}
              loading={customersLoading}
              noOptionsText="No customers found"
              helperText="Search by customer name."
              sx={{ maxWidth: 520 }}
            />
            {selectedCustomerId && (
              <Alert severity="info" sx={{ fontFamily: "var(--theo-font-sans)" }}>
                MSP-wide playbooks continue to apply. Customer-specific playbooks add instructions and do not override or disable them.
              </Alert>
            )}
          </Stack>
        </Paper>
      )}

      {configurationScope === "customer" && !selectedCustomerId ? (
        <Box
          sx={{
            p: 5,
            borderRadius: "10px",
            border: "1px dashed #D1D5DB",
            background: "#FFFFFF",
            textAlign: "center",
          }}
        >
          <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: "18px", fontWeight: 600, color: "#374151", mb: 1 }}>
            Select a customer to configure playbooks
          </Typography>
          <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: "14px", color: "#6B7280" }}>
            MSP-wide playbooks remain active while you add customer-specific General instructions.
          </Typography>
        </Box>
      ) : !hasEntries ? (
        <Box
          sx={{
            p: 5,
            borderRadius: "10px",
            border: "1px dashed #D1D5DB",
            background: "#FFFFFF",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontFamily: "var(--theo-font-sans)",
              fontSize: "18px",
              fontWeight: 600,
              color: "#374151",
              mb: 1,
            }}
          >
            No enabled applications or platforms found
          </Typography>
          <Typography
            sx={{
              fontFamily: "var(--theo-font-sans)",
              fontSize: "14px",
              color: "#6B7280",
            }}
          >
            Enable an application or platform first, then its playbook editor will
            appear here automatically.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={3}>
          {entries.map((entry) => {
            const entryKey = getEntryKey(entry);
            return (
              <Box
                key={entryKey}
                sx={{
                  p: 3,
                  borderRadius: "10px",
                  background: "#FFFFFF",
                  border: "1px solid #E5E7EB",
                  boxShadow: "0 12px 32px rgba(15, 23, 42, 0.05)",
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  justifyContent="space-between"
                  sx={{ mb: 2 }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: "var(--theo-font-sans)",
                        fontSize: "21px",
                        fontWeight: 700,
                        color: "#111827",
                        mb: 0.75,
                      }}
                    >
                      {getEntryTitle(entry)}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip
                        label={
                          entry.ownership_scope === "customer"
                            ? "Customer-specific"
                            : entry.entity_type === "general"
                            ? "General"
                            : entry.entity_type === "platform"
                              ? "Platform"
                              : "Application"
                        }
                        size="small"
                        sx={{
                          fontFamily: "var(--theo-font-sans)",
                          fontWeight: 700,
                          background: "#F3F4F6",
                          color: "#374151",
                        }}
                      />
                      <Chip
                        label={getEntrySubtitle(entry)}
                        size="small"
                        sx={{
                          fontFamily: "var(--theo-font-sans)",
                          background: "#F9F5F1",
                          color: "#705D4B",
                        }}
                      />
                    </Stack>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={() => handleAddPlaybook(entry)}
                    sx={{
                      alignSelf: { xs: "flex-start", md: "center" },
                      fontFamily: "var(--theo-font-sans)",
                      fontWeight: 700,
                      textTransform: "none",
                      backgroundColor: "#705D4B",
                      "&:hover": { backgroundColor: "#5B4A3A" },
                    }}
                  >
                    {entry.ownership_scope === "customer" ? "Add customer playbook" : "Add playbook"}
                  </Button>
                </Stack>

                <Stack spacing={2.5}>
                  {entry.skills.length === 0 && (
                    <Alert severity="info" sx={{ fontFamily: "var(--theo-font-sans)" }}>
                      No playbooks configured yet. Add one to teach Theo when these
                      instructions apply.
                    </Alert>
                  )}
                  {entry.skills.map((playbook, playbookIndex) => {
                    const playbookKey = getPlaybookKey(entry, playbook, playbookIndex);
                    const isBusy = busyKey === playbookKey;
                    const isDirty = dirtyPlaybooks[`${entryKey}:${playbookIndex}`] ?? false;
                    const editorMode = editorModes[playbookKey] ?? "write";
                    const isExpanded = expandedPlaybooks[playbookKey] ?? false;
                    const markdownHtml = renderMarkdown(playbook.skill_text ?? "");

                    return (
                      <Box
                        key={playbookKey}
                        sx={{
                          p: 2.5,
                          borderRadius: "14px",
                          border: "1px solid #E5E7EB",
                          background: playbook.is_disabled ? "#F9FAFB" : "#FFFFFF",
                        }}
                      >
                        <Stack spacing={2}>
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1.5}
                            justifyContent="space-between"
                            alignItems={{ xs: "stretch", sm: "flex-start" }}
                          >
                            <Stack direction="row" spacing={1.25} alignItems="flex-start">
                              <IconButton
                                size="small"
                                onClick={() => togglePlaybookExpanded(playbookKey)}
                                aria-label={isExpanded ? "Collapse playbook" : "Expand playbook"}
                                sx={{
                                  mt: -0.5,
                                  color: "#705D4B",
                                  transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                                  transition: "transform 160ms ease",
                                }}
                              >
                                <ExpandMoreOutlinedIcon />
                              </IconButton>
                              <Box>
                                <Typography
                                  sx={{
                                    fontFamily: "var(--theo-font-sans)",
                                    fontSize: "17px",
                                    fontWeight: 700,
                                    color: "#111827",
                                  }}
                                >
                                  {playbook.name || "Untitled playbook"}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontFamily: "var(--theo-font-sans)",
                                    fontSize: "13px",
                                    color: "#6B7280",
                                    mt: 0.25,
                                    maxWidth: "760px",
                                  }}
                                >
                                  {playbook.description || "No description yet."}
                                </Typography>
                              </Box>
                            </Stack>

                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              <Chip
                                label={getPlaybookSourceLabel(playbook)}
                                size="small"
                                sx={{
                                  fontFamily: "var(--theo-font-sans)",
                                  background: playbook.is_disabled
                                    ? "#FEF2F2"
                                    : playbook.is_global_default
                                      ? "#EEF2FF"
                                      : "#ECFDF5",
                                  color: playbook.is_disabled
                                    ? "#B91C1C"
                                    : playbook.is_global_default
                                      ? "#3730A3"
                                      : "#047857",
                                }}
                              />
                              <Chip
                                label={playbook.enabled ? "Enabled" : "Disabled"}
                                size="small"
                                sx={{
                                  fontFamily: "var(--theo-font-sans)",
                                  background: playbook.enabled ? "#F0FDF4" : "#FEF2F2",
                                  color: playbook.enabled ? "#166534" : "#B91C1C",
                                }}
                              />
                              {isDirty && (
                                <Chip
                                  label="Unsaved changes"
                                  size="small"
                                  sx={{
                                    fontFamily: "var(--theo-font-sans)",
                                    background: "#FFF7ED",
                                    color: "#C2410C",
                                  }}
                                />
                              )}
                              {playbook.skill_slug && (
                                <Chip
                                  label={playbook.skill_slug}
                                  size="small"
                                  sx={{ fontFamily: "var(--theo-font-sans)", background: "#F3F4F6" }}
                                />
                              )}
                              {isGeneralPlaybookEntry(entry) &&
                                (playbook.theo_categories || []).map((selection) => {
                                  const label = formatTheoCategorySelectionLabel(selection);
                                  return label ? (
                                    <Chip
                                      key={`${selection.l1}:${selection.l2 || ""}`}
                                      label={label}
                                      size="small"
                                      sx={{ fontFamily: "var(--theo-font-sans)", background: "#F5F3F0", color: "#705D4B" }}
                                    />
                                  ) : null;
                                })}
                            </Stack>
                          </Stack>

                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Stack spacing={2} sx={{ pt: 1 }}>
                              <Divider />
                          <TextField
                            label="Name"
                            value={playbook.name}
                            onChange={(event) =>
                              updatePlaybookAtIndex(entryKey, playbookIndex, {
                                name: event.target.value,
                              })
                            }
                            fullWidth
                          />
                          <TextField
                            label="When to use"
                            value={playbook.description}
                            onChange={(event) =>
                              updatePlaybookAtIndex(entryKey, playbookIndex, {
                                description: event.target.value,
                              })
                            }
                            fullWidth
                            multiline
                            minRows={2}
                            helperText="Describe the requests or situations where Theo should load this playbook."
                          />
                          {isGeneralPlaybookEntry(entry) && (
                            <Stack spacing={1}>
                              <FormControlLabel
                                sx={{
                                  ml: 0,
                                  alignItems: "center",
                                  "& .MuiFormControlLabel-label": {
                                    fontFamily: "var(--theo-font-sans)",
                                    fontSize: "14px",
                                    color: "#374151",
                                  },
                                }}
                                control={
                                  <Checkbox
                                    checked={
                                      pinEnabled[playbookKey] ??
                                      (playbook.theo_categories || []).length > 0
                                    }
                                    onChange={(event) => {
                                      const checked = event.target.checked;
                                      setPinEnabled((current) => ({
                                        ...current,
                                        [playbookKey]: checked,
                                      }));
                                      if (!checked) {
                                        updatePlaybookAtIndex(entryKey, playbookIndex, {
                                          theo_categories: [],
                                        });
                                      }
                                    }}
                                  />
                                }
                                label="Pin playbook to category"
                              />
                              {(pinEnabled[playbookKey] ??
                                (playbook.theo_categories || []).length > 0) && (
                                <TheoCategoryAutocomplete
                                  value={playbook.theo_categories || []}
                                  options={theoTaxonomyOptions}
                                  onChange={(selections) =>
                                    updatePlaybookAtIndex(entryKey, playbookIndex, {
                                      theo_categories: selections,
                                    })
                                  }
                                  helperText="Auto Mode always loads this playbook when the ticket matches."
                                />
                              )}
                            </Stack>
                          )}

                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography
                              sx={{
                                fontFamily: "var(--theo-font-sans)",
                                fontSize: "13px",
                                color: "#6B7280",
                              }}
                            >
                              Write Markdown playbook instructions, normalize them into a procedural Theo format, then preview the rendered output.
                            </Typography>
                            <Stack direction="row" spacing={1}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<AutoAwesomeOutlinedIcon />}
                                onClick={() => handleNormalize(entry, playbook, playbookIndex)}
                                disabled={isBusy || !(playbook.skill_text || "").trim()}
                                sx={{
                                  minWidth: 118,
                                  fontFamily: "var(--theo-font-sans)",
                                  fontWeight: 700,
                                  textTransform: "none",
                                  borderColor: "rgba(112, 93, 75, 0.4)",
                                  color: "#705D4B",
                                }}
                              >
                                Normalize
                              </Button>
                              <Button
                                variant={editorMode === "write" ? "contained" : "outlined"}
                                size="small"
                                onClick={() => setEditorMode(playbookKey, "write")}
                                sx={{
                                  minWidth: 88,
                                  fontFamily: "var(--theo-font-sans)",
                                  fontWeight: 700,
                                  textTransform: "none",
                                  backgroundColor: editorMode === "write" ? "#705D4B" : undefined,
                                  "&:hover": {
                                    backgroundColor: editorMode === "write" ? "#5B4A3A" : undefined,
                                  },
                                }}
                              >
                                Write
                              </Button>
                              <Button
                                variant={editorMode === "preview" ? "contained" : "outlined"}
                                size="small"
                                onClick={() => setEditorMode(playbookKey, "preview")}
                                sx={{
                                  minWidth: 88,
                                  fontFamily: "var(--theo-font-sans)",
                                  fontWeight: 700,
                                  textTransform: "none",
                                  backgroundColor: editorMode === "preview" ? "#705D4B" : undefined,
                                  "&:hover": {
                                    backgroundColor: editorMode === "preview" ? "#5B4A3A" : undefined,
                                  },
                                }}
                              >
                                Preview
                              </Button>
                            </Stack>
                          </Stack>

                          {editorMode === "write" ? (
                            <TextField
                              fullWidth
                              multiline
                              minRows={10}
                              value={playbook.skill_text ?? ""}
                              onChange={(event) =>
                                updatePlaybookAtIndex(entryKey, playbookIndex, {
                                  skill_text: event.target.value,
                                })
                              }
                              placeholder={`Write the ${playbook.name || formatAppName(entry.name)} playbook instructions here...`}
                              sx={{
                                "& .MuiInputBase-root": {
                                  fontFamily:
                                    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                                  fontSize: "14px",
                                  lineHeight: 1.7,
                                  alignItems: "flex-start",
                                  background: "#FCFCFD",
                                },
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                minHeight: 220,
                                px: 2.25,
                                py: 2,
                                borderRadius: "6px",
                                background: "#FCFCFD",
                                border: "1px solid rgba(0, 0, 0, 0.23)",
                                fontFamily: "var(--theo-font-sans)",
                                color: "#1F2937",
                                "& h1, & h2, & h3, & h4": {
                                  fontFamily: "var(--theo-font-sans)",
                                  fontWeight: 700,
                                  lineHeight: 1.25,
                                  mt: 2,
                                  mb: 1,
                                },
                                "& p, & li": { lineHeight: 1.75, fontSize: "14px" },
                                "& ul, & ol": { pl: 3 },
                                "& code": {
                                  fontFamily:
                                    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                                  background: "#F3F4F6",
                                  px: 0.6,
                                  py: 0.25,
                                  borderRadius: "4px",
                                  fontSize: "0.9em",
                                },
                                "& pre": {
                                  background: "#111827",
                                  color: "#F9FAFB",
                                  p: 2,
                                  borderRadius: "6px",
                                  overflowX: "auto",
                                },
                                "& pre code": { background: "transparent", color: "inherit", p: 0 },
                                "& blockquote": {
                                  m: 0,
                                  pl: 2,
                                  borderLeft: "4px solid #D1D5DB",
                                  color: "#4B5563",
                                },
                              }}
                              dangerouslySetInnerHTML={{ __html: markdownHtml }}
                            />
                          )}

                          <Divider />

                          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                            <Button
                              variant="outlined"
                              onClick={() => handleToggleEnabled(entry, playbook, playbookIndex)}
                              disabled={isBusy}
                              sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 700, textTransform: "none" }}
                            >
                              {playbook.enabled ? "Disable" : "Enable"}
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              startIcon={<DeleteOutlineOutlinedIcon />}
                              onClick={() => handleDelete(entry, playbook, playbookIndex)}
                              disabled={isBusy || (!playbook.skill_id && Boolean(playbook.global_skill_id))}
                              sx={{
                                fontFamily: "var(--theo-font-sans)",
                                fontWeight: 700,
                                textTransform: "none",
                                borderColor: "rgba(211, 47, 47, 0.35)",
                              }}
                            >
                              Delete
                            </Button>
                            <Button
                              variant="contained"
                              startIcon={<SaveOutlinedIcon />}
                              onClick={() => handleSave(entry, playbook, playbookIndex)}
                              disabled={isBusy}
                              sx={{
                                fontFamily: "var(--theo-font-sans)",
                                fontWeight: 700,
                                textTransform: "none",
                                backgroundColor: "#705D4B",
                                "&:hover": { backgroundColor: "#5B4A3A" },
                              }}
                            >
                              Save
                            </Button>
                          </Stack>
                            </Stack>
                          </Collapse>
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}

      <Snackbar
        open={alert.open}
        autoHideDuration={4500}
        onClose={() => setAlert((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={alert.severity}
          onClose={() => setAlert((current) => ({ ...current, open: false }))}
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default ApplicationPlaybooks;
